import React, { useState } from 'react';
import {
    Task, PhysicalUnit, Level, Responsible, WorkGroupTemplate, ReviewerGroup, ProtocolConfig
} from '../../types';
import { cleanTaskName, flattenTasks } from '../../utils/planningUtils';

interface WorkGroupManagerModalProps {
    isOpen: boolean;
    onClose: () => void;
    tasks: Task[];
    units: PhysicalUnit[];
    levels: Level[];
    sortedResponsibles: Responsible[];
    protocolConfigs: Record<string, ProtocolConfig>;
    setProtocolConfigs: React.Dispatch<React.SetStateAction<Record<string, ProtocolConfig>>>;
    workGroupTemplates: WorkGroupTemplate[];
    setWorkGroupTemplates: React.Dispatch<React.SetStateAction<WorkGroupTemplate[]>>;
}

export const WorkGroupManagerModal: React.FC<WorkGroupManagerModalProps> = ({
    isOpen,
    onClose,
    tasks,
    units,
    levels,
    sortedResponsibles,
    protocolConfigs,
    setProtocolConfigs,
    workGroupTemplates = [],
    setWorkGroupTemplates
}) => {
    const [selectedTab, setSelectedTab] = useState<'templates' | 'assignments'>('assignments');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);

    // Modal state for editing a template or a task group
    const [editingTemplate, setEditingTemplate] = useState<WorkGroupTemplate | null>(null);
    const [unitSelectorOpen, setUnitSelectorOpen] = useState(false);

    // Filter tasks for assignment tab
    const flatGanttTasks = React.useMemo(() => {
        return flattenTasks(tasks).filter(t => !t.isWeeklySubtask);
    }, [tasks]);

    const filteredTasks = React.useMemo(() => {
        if (!searchTerm.trim()) return flatGanttTasks;
        const norm = searchTerm.toLowerCase();
        return flatGanttTasks.filter(t => 
            t.name.toLowerCase().includes(norm) ||
            String(t.id_original_project || '').toLowerCase().includes(norm)
        );
    }, [flatGanttTasks, searchTerm]);

    if (!isOpen) return null;

    // --- TEMPLATE CRUD HANDLERS ---
    const handleCreateTemplate = () => {
        const newTpl: WorkGroupTemplate = {
            id: 'tpl_' + Math.random().toString(36).substr(2, 9),
            name: `Grupo de Trabajo ${workGroupTemplates.length + 1}`,
            unitIds: [],
            reviewers: [
                { id: '', name: '', generatesProgress: false, generatesPayment: false }
            ]
        };
        setEditingTemplate(newTpl);
    };

    const handleSaveTemplate = (tpl: WorkGroupTemplate) => {
        if (!tpl.name.trim()) {
            alert('Debe ingresar un nombre para el grupo de trabajo.');
            return;
        }
        setWorkGroupTemplates(prev => {
            const exists = prev.some(g => g.id === tpl.id);
            if (exists) {
                return prev.map(g => g.id === tpl.id ? tpl : g);
            }
            return [...prev, tpl];
        });
        setEditingTemplate(null);
    };

    const handleDeleteTemplate = (tplId: string) => {
        if (window.confirm('¿Está seguro de eliminar esta plantilla de grupo de trabajo?')) {
            setWorkGroupTemplates(prev => prev.filter(g => g.id !== tplId));
            if (editingTemplate?.id === tplId) {
                setEditingTemplate(null);
            }
        }
    };

    // --- ASSIGNMENT TO GANTT TASKS HANDLERS ---
    const handleAssignTemplateToTasks = (tpl: WorkGroupTemplate, targetTaskIds: string[]) => {
        if (targetTaskIds.length === 0) {
            alert('Seleccione al menos una partida de la lista.');
            return;
        }

        setProtocolConfigs(prev => {
            const updated = { ...prev };
            targetTaskIds.forEach(taskId => {
                const current = updated[taskId] || {
                    taskId,
                    applies: false,
                    reviewerGroups: [],
                    code: '',
                    protocolName: '',
                    items: []
                };

                const currentGroups = current.reviewerGroups ? [...current.reviewerGroups] : [];
                // Check if already assigned (by matching ID or exact name)
                const exists = currentGroups.some(g => g.id === tpl.id || g.name === tpl.name);
                if (!exists) {
                    currentGroups.push({
                        id: tpl.id,
                        name: tpl.name,
                        unitIds: [...tpl.unitIds],
                        reviewers: tpl.reviewers.map(r => ({ ...r }))
                    });
                } else {
                    // Update existing
                    const idx = currentGroups.findIndex(g => g.id === tpl.id || g.name === tpl.name);
                    currentGroups[idx] = {
                        id: tpl.id,
                        name: tpl.name,
                        unitIds: [...tpl.unitIds],
                        reviewers: tpl.reviewers.map(r => ({ ...r }))
                    };
                }

                updated[taskId] = {
                    ...current,
                    reviewerGroups: currentGroups
                };
            });
            return updated;
        });

        setSelectedTaskIds([]);
    };

    const handleRemoveGroupFromTask = (taskId: string, groupId: string) => {
        setProtocolConfigs(prev => {
            const current = prev[taskId];
            if (!current || !current.reviewerGroups) return prev;
            return {
                ...prev,
                [taskId]: {
                    ...current,
                    reviewerGroups: current.reviewerGroups.filter(g => g.id !== groupId)
                }
            };
        });
    };

    const toggleSelectTask = (taskId: string) => {
        setSelectedTaskIds(prev => 
            prev.includes(taskId) ? prev.filter(id => id !== taskId) : [...prev, taskId]
        );
    };

    const toggleSelectAllVisibleTasks = () => {
        const selectable = filteredTasks.filter(t => t.type !== 'folder');
        const allSelected = selectable.every(t => selectedTaskIds.includes(t.id));
        if (allSelected) {
            setSelectedTaskIds(prev => prev.filter(id => !selectable.some(t => t.id === id)));
        } else {
            const newIds = new Set([...selectedTaskIds, ...selectable.map(t => t.id)]);
            setSelectedTaskIds(Array.from(newIds));
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white dark:bg-[#1a2634] rounded-3xl shadow-2xl w-full max-w-6xl overflow-hidden border border-slate-200 dark:border-slate-800 flex flex-col h-[88vh] animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="p-5 px-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-900/50 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="size-10 rounded-2xl bg-blue-100 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center shadow-inner">
                            <span className="material-symbols-outlined text-[24px]">group_work</span>
                        </div>
                        <div>
                            <h3 className="text-lg font-black text-slate-800 dark:text-white uppercase tracking-tight flex items-center gap-2">
                                Grupos de Trabajo y Responsables
                            </h3>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                Preconfigure sectores, cuadrillas y revisores para aplicarlos de forma masiva a las partidas y a Calidad.
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Tabs */}
                        <div className="flex bg-slate-200/70 dark:bg-slate-800 p-1 rounded-xl">
                            <button
                                type="button"
                                onClick={() => setSelectedTab('assignments')}
                                className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                                    selectedTab === 'assignments'
                                        ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm'
                                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                                }`}
                            >
                                <span className="material-symbols-outlined text-[16px]">assignment_turned_in</span>
                                Asignación por Partida
                            </button>
                            <button
                                type="button"
                                onClick={() => setSelectedTab('templates')}
                                className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                                    selectedTab === 'templates'
                                        ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm'
                                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                                }`}
                            >
                                <span className="material-symbols-outlined text-[16px]">folder_special</span>
                                Plantillas de Grupos ({workGroupTemplates.length})
                            </button>
                        </div>

                        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors p-1.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800">
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>
                </div>

                {/* Tab 1: Asignación por Partida */}
                {selectedTab === 'assignments' && (
                    <div className="flex-1 flex flex-col overflow-hidden p-6 gap-4">
                        {/* Actions & Filters Bar */}
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                            <div className="flex items-center gap-3 flex-1 min-w-[260px] max-w-md">
                                <div className="relative w-full">
                                    <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">search</span>
                                    <input
                                        type="text"
                                        placeholder="Buscar por código o partida..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-9 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 text-slate-800 dark:text-white"
                                    />
                                </div>
                            </div>

                            {/* Batch Assignment Dropdown */}
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-slate-500">
                                    {selectedTaskIds.length} seleccionada(s)
                                </span>
                                {workGroupTemplates.length === 0 ? (
                                    <button
                                        type="button"
                                        onClick={() => { setSelectedTab('templates'); handleCreateTemplate(); }}
                                        className="px-3.5 py-2 bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 text-xs font-bold rounded-xl border border-blue-200 dark:border-blue-800 hover:bg-blue-100 transition-colors flex items-center gap-1.5"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">add_circle</span>
                                        Crear Primer Grupo
                                    </button>
                                ) : (
                                    <div className="relative group">
                                        <button
                                            type="button"
                                            disabled={selectedTaskIds.length === 0}
                                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:pointer-events-none text-white text-xs font-bold rounded-xl shadow-md shadow-blue-500/20 transition-all flex items-center gap-1.5 active:scale-95"
                                        >
                                            <span className="material-symbols-outlined text-[16px]">link</span>
                                            Asignar Grupo Establecido
                                            <span className="material-symbols-outlined text-[16px]">expand_more</span>
                                        </button>
                                        <div className="hidden group-hover:block absolute right-0 top-full mt-1 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 py-1.5 z-20 w-64 animate-in fade-in zoom-in-95 duration-100">
                                            <p className="px-3 py-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-slate-700">
                                                Seleccionar Grupo:
                                            </p>
                                            <div className="max-h-56 overflow-y-auto">
                                                {workGroupTemplates.map(tpl => (
                                                    <button
                                                        key={tpl.id}
                                                        type="button"
                                                        onClick={() => handleAssignTemplateToTasks(tpl, selectedTaskIds)}
                                                        className="w-full text-left px-3 py-2 text-xs text-slate-700 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-slate-700/60 flex items-center justify-between"
                                                    >
                                                        <span className="font-semibold truncate">{tpl.name}</span>
                                                        <span className="text-[10px] text-slate-400 font-mono ml-2 shrink-0">{tpl.unitIds.length} unid.</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Gantt Tasks Table */}
                        <div className="flex-1 overflow-auto border border-slate-200 dark:border-slate-800 rounded-2xl bg-white dark:bg-[#1a2634]">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-slate-50 dark:bg-slate-800/80 sticky top-0 z-10 border-b border-slate-200 dark:border-slate-700 text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                    <tr>
                                        <th className="p-3 w-12 text-center">
                                            <input
                                                type="checkbox"
                                                onChange={toggleSelectAllVisibleTasks}
                                                checked={filteredTasks.filter(t => t.type !== 'folder').length > 0 && filteredTasks.filter(t => t.type !== 'folder').every(t => selectedTaskIds.includes(t.id))}
                                                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 size-4 cursor-pointer"
                                            />
                                        </th>
                                        <th className="p-3 w-16 text-center font-mono">#</th>
                                        <th className="p-3">Partida / Tarea (Carta Gantt)</th>
                                        <th className="p-3 w-40 text-center">Aplica Calidad</th>
                                        <th className="p-3 min-w-[280px]">Grupos de Trabajo Asignados</th>
                                        <th className="p-3 w-44 text-right">Acción</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60 text-xs">
                                    {filteredTasks.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className="py-12 text-center text-slate-400 italic">
                                                No se encontraron partidas con el criterio ingresado.
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredTasks.map(task => {
                                            const isFolder = task.type === 'folder';
                                            const ganttNumber = task.id_original_project !== undefined && task.id_original_project !== null && task.id_original_project !== ''
                                                ? task.id_original_project
                                                : '-';
                                            const config = protocolConfigs[task.id];
                                            const groups = config?.reviewerGroups || [];
                                            const isSelected = selectedTaskIds.includes(task.id);

                                            if (isFolder) {
                                                return (
                                                    <tr key={task.id} className="bg-slate-100/70 dark:bg-slate-800/40 font-bold">
                                                        <td className="p-3 text-center text-slate-300">-</td>
                                                        <td className="p-3 text-center font-mono text-slate-500">{ganttNumber}</td>
                                                        <td colSpan={4} className="p-3 text-slate-800 dark:text-white uppercase tracking-wider text-[11px]">
                                                            📁 {task.name}
                                                        </td>
                                                    </tr>
                                                );
                                            }

                                            return (
                                                <tr
                                                    key={task.id}
                                                    className={`hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors ${
                                                        isSelected ? 'bg-blue-50/40 dark:bg-blue-950/20' : ''
                                                    }`}
                                                >
                                                    <td className="p-3 text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => toggleSelectTask(task.id)}
                                                            className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 size-4 cursor-pointer"
                                                        />
                                                    </td>
                                                    <td className="p-3 text-center font-mono text-slate-500 font-bold">{ganttNumber}</td>
                                                    <td className="p-3 font-semibold text-slate-800 dark:text-slate-200">
                                                        <div className="flex items-center gap-2">
                                                            <span>{cleanTaskName(task.name)}</span>
                                                            {task.children && task.children.some(c => c.isWeeklySubtask && !c.isDeleted) && (
                                                                <span 
                                                                    className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-200 dark:border-amber-800"
                                                                    title="Partida desglosada en la Planificación Semanal. Sus subtareas heredarán automáticamente los responsables aquí asignados."
                                                                >
                                                                    Desglosada ({task.children.filter(c => c.isWeeklySubtask && !c.isDeleted).length} subtareas herederas)
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="p-3 text-center">
                                                        {config?.applies ? (
                                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                                                Sí Aplica
                                                            </span>
                                                        ) : (
                                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                                                                No
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="p-3">
                                                        {groups.length === 0 ? (
                                                            <span className="text-slate-400 italic text-[11px]">Sin grupos asignados</span>
                                                        ) : (
                                                            <div className="flex flex-wrap gap-1.5">
                                                                {groups.map(g => (
                                                                    <span
                                                                        key={g.id}
                                                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[14px]">groups</span>
                                                                        <span>{g.name || 'Grupo'}</span>
                                                                        <span className="text-[9px] opacity-75 font-mono">({g.unitIds.length} un.)</span>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleRemoveGroupFromTask(task.id, g.id)}
                                                                            className="text-blue-400 hover:text-red-500 ml-0.5"
                                                                            title="Desvincular Grupo de esta partida"
                                                                        >
                                                                            <span className="material-symbols-outlined text-[13px]">close</span>
                                                                        </button>
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="p-3 text-right">
                                                        <div className="relative inline-block group">
                                                            <button
                                                                type="button"
                                                                className="px-2.5 py-1.5 bg-slate-100 hover:bg-blue-50 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 hover:text-blue-600 rounded-lg text-xs font-bold transition-all flex items-center gap-1 ml-auto"
                                                            >
                                                                <span className="material-symbols-outlined text-[16px]">group_add</span>
                                                                <span>+ Grupo</span>
                                                            </button>
                                                            <div className="hidden group-hover:block absolute right-0 top-full mt-1 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 py-1.5 z-20 w-60 text-left animate-in fade-in zoom-in-95 duration-100">
                                                                <p className="px-3 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-slate-700">
                                                                    Grupos Establecidos:
                                                                </p>
                                                                {workGroupTemplates.length === 0 ? (
                                                                    <div className="p-3 text-center text-xs text-slate-400 italic">
                                                                        No hay plantillas creadas.
                                                                    </div>
                                                                ) : (
                                                                    <div className="max-h-48 overflow-y-auto">
                                                                        {workGroupTemplates.map(tpl => (
                                                                            <button
                                                                                key={tpl.id}
                                                                                type="button"
                                                                                onClick={() => handleAssignTemplateToTasks(tpl, [task.id])}
                                                                                className="w-full text-left px-3 py-1.5 text-xs text-slate-700 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-slate-700 flex items-center justify-between"
                                                                            >
                                                                                <span className="font-medium truncate">{tpl.name}</span>
                                                                                <span className="text-[10px] text-slate-400 font-mono ml-2 shrink-0">{tpl.unitIds.length} un.</span>
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                <div className="border-t border-slate-100 dark:border-slate-700 mt-1 pt-1">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => { setSelectedTab('templates'); handleCreateTemplate(); }}
                                                                        className="w-full text-left px-3 py-1.5 text-xs text-blue-600 dark:text-blue-400 font-bold hover:bg-blue-50 dark:hover:bg-slate-700 flex items-center gap-1.5"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[15px]">add</span>
                                                                        Nuevo Grupo Maestro
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Tab 2: Biblioteca de Plantillas de Grupos */}
                {selectedTab === 'templates' && (
                    <div className="flex-1 flex overflow-hidden p-6 gap-6">
                        {/* Left List of Templates */}
                        <div className="w-1/3 border-r border-slate-100 dark:border-slate-800 pr-6 flex flex-col gap-4">
                            <div className="flex items-center justify-between">
                                <h4 className="text-xs font-black text-slate-500 uppercase tracking-wider">
                                    Grupos Establecidos ({workGroupTemplates.length})
                                </h4>
                                <button
                                    type="button"
                                    onClick={handleCreateTemplate}
                                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md shadow-blue-500/20 transition-all flex items-center gap-1 active:scale-95"
                                >
                                    <span className="material-symbols-outlined text-[16px]">add</span>
                                    Nuevo Grupo
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto space-y-2.5">
                                {workGroupTemplates.length === 0 ? (
                                    <div className="p-8 text-center text-slate-400 italic text-xs bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800">
                                        No hay grupos maestros creados. Haga clic en "+ Nuevo Grupo" para crear el primero.
                                    </div>
                                ) : (
                                    workGroupTemplates.map(tpl => {
                                        const isSelected = editingTemplate?.id === tpl.id;
                                        return (
                                            <div
                                                key={tpl.id}
                                                onClick={() => setEditingTemplate(JSON.parse(JSON.stringify(tpl)))}
                                                className={`p-3.5 rounded-2xl border transition-all cursor-pointer ${
                                                    isSelected
                                                        ? 'bg-blue-50/70 dark:bg-blue-950/40 border-blue-500 shadow-sm ring-1 ring-blue-500'
                                                        : 'bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-750 border-slate-200 dark:border-slate-700'
                                                }`}
                                            >
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="flex-1 min-w-0">
                                                        <h5 className="text-xs font-bold text-slate-800 dark:text-white truncate">
                                                            {tpl.name}
                                                        </h5>
                                                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                                                                🏠 {tpl.unitIds.length} Unidades
                                                            </span>
                                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                                                                👤 {tpl.reviewers.length} Revisor(es)
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(tpl.id); }}
                                                        className="text-slate-400 hover:text-red-500 p-1 transition-colors"
                                                        title="Eliminar plantilla"
                                                    >
                                                        <span className="material-symbols-outlined text-[18px]">delete</span>
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        {/* Right Detail/Editor */}
                        <div className="flex-1 flex flex-col overflow-y-auto">
                            {!editingTemplate ? (
                                <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-8">
                                    <span className="material-symbols-outlined text-[48px] text-slate-300 mb-2">dashboard_customize</span>
                                    <p className="text-sm font-semibold">Seleccione un grupo de la lista para editarlo o cree uno nuevo.</p>
                                </div>
                            ) : (
                                <div className="space-y-6 flex-1 flex flex-col">
                                    {/* Name input */}
                                    <div>
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1.5">
                                            Nombre del Grupo de Trabajo
                                        </label>
                                        <input
                                            type="text"
                                            value={editingTemplate.name}
                                            onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                                            placeholder="Ej: Cuadrilla Muros - Sector Norte..."
                                            className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-blue-500 text-slate-800 dark:text-white"
                                        />
                                    </div>

                                    {/* Units selector preview */}
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">
                                                Unidades Físicas Asignadas ({editingTemplate.unitIds.length})
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() => setUnitSelectorOpen(true)}
                                                className="px-3 py-1 bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 rounded-lg text-xs font-bold hover:bg-blue-100 transition-colors flex items-center gap-1 border border-blue-200 dark:border-blue-800"
                                            >
                                                <span className="material-symbols-outlined text-[15px]">domain</span>
                                                Seleccionar Unidades
                                            </button>
                                        </div>
                                        <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-slate-800 max-h-36 overflow-y-auto flex flex-wrap gap-1.5">
                                            {editingTemplate.unitIds.length === 0 ? (
                                                <span className="text-xs text-slate-400 italic">No hay unidades seleccionadas. Pulse 'Seleccionar Unidades'.</span>
                                            ) : (
                                                editingTemplate.unitIds.map(uid => (
                                                    <span key={uid} className="px-2 py-0.5 bg-blue-100/70 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 text-[10px] font-bold rounded-lg border border-blue-200 dark:border-blue-800">
                                                        {units.find(u => u.id === uid)?.name || uid}
                                                    </span>
                                                ))
                                            )}
                                        </div>
                                    </div>

                                    {/* Reviewers List */}
                                    <div className="flex-1 flex flex-col">
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">
                                                Revisores / Responsables
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setEditingTemplate({
                                                        ...editingTemplate,
                                                        reviewers: [
                                                            ...editingTemplate.reviewers,
                                                            { id: '', name: '', generatesProgress: false, generatesPayment: false }
                                                        ]
                                                    });
                                                }}
                                                className="px-3 py-1 bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-400 rounded-lg text-xs font-bold hover:bg-green-100 transition-colors flex items-center gap-1 border border-green-200 dark:border-green-800"
                                            >
                                                <span className="material-symbols-outlined text-[15px]">add</span>
                                                Añadir Revisor
                                            </button>
                                        </div>

                                        <div className="flex items-center gap-4 mb-2 px-2 text-[10px] font-bold text-slate-400 uppercase">
                                            <span className="flex-1">Revisor / Cargo</span>
                                            <span className="w-24 text-center">Genera Avance</span>
                                            <span className="w-24 text-center">Genera Pago</span>
                                            <span className="w-8"></span>
                                        </div>

                                        <div className="space-y-2.5 overflow-y-auto pr-1 flex-1">
                                            {editingTemplate.reviewers.map((rev, idx) => (
                                                <div key={idx} className="flex items-center gap-4 p-2 bg-slate-50 dark:bg-slate-800/80 rounded-xl border border-slate-200 dark:border-slate-700">
                                                    <div className="flex-1">
                                                        <select
                                                            value={rev.id}
                                                            onChange={(e) => {
                                                                const chosenId = e.target.value;
                                                                const chosenResp = sortedResponsibles.find(r => r.id === chosenId);
                                                                const updated = [...editingTemplate.reviewers];
                                                                updated[idx] = {
                                                                    ...updated[idx],
                                                                    id: chosenId,
                                                                    name: chosenResp?.name || ''
                                                                };
                                                                setEditingTemplate({ ...editingTemplate, reviewers: updated });
                                                            }}
                                                            className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-xs font-semibold outline-none focus:ring-2 focus:ring-blue-500 text-slate-800 dark:text-white"
                                                        >
                                                            <option value="">Seleccionar Revisor {idx + 1}...</option>
                                                            {sortedResponsibles.map(r => (
                                                                <option key={r.id} value={r.id}>
                                                                    {r.name} ({r.role || r.companyName || 'Sin Cargo'})
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="w-24 flex justify-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={rev.generatesProgress}
                                                            onChange={(e) => {
                                                                const checked = e.target.checked;
                                                                const updated = editingTemplate.reviewers.map((r, i) => ({
                                                                    ...r,
                                                                    generatesProgress: checked ? (i === idx) : (i === idx ? false : r.generatesProgress)
                                                                }));
                                                                setEditingTemplate({ ...editingTemplate, reviewers: updated });
                                                            }}
                                                            className="size-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                                        />
                                                    </div>
                                                    <div className="w-24 flex justify-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={rev.generatesPayment}
                                                            onChange={(e) => {
                                                                const checked = e.target.checked;
                                                                const updated = editingTemplate.reviewers.map((r, i) => ({
                                                                    ...r,
                                                                    generatesPayment: checked ? (i === idx) : (i === idx ? false : r.generatesPayment)
                                                                }));
                                                                setEditingTemplate({ ...editingTemplate, reviewers: updated });
                                                            }}
                                                            className="size-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                                        />
                                                    </div>
                                                    <div className="w-8 flex justify-center">
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const updated = editingTemplate.reviewers.filter((_, i) => i !== idx);
                                                                setEditingTemplate({ ...editingTemplate, reviewers: updated });
                                                            }}
                                                            className="size-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                                                            title="Eliminar revisor"
                                                        >
                                                            <span className="material-symbols-outlined text-[18px]">delete</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                                        <button
                                            type="button"
                                            onClick={() => setEditingTemplate(null)}
                                            className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleSaveTemplate(editingTemplate)}
                                            className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md shadow-blue-500/20 active:scale-95 transition-all flex items-center gap-1.5"
                                        >
                                            <span className="material-symbols-outlined text-[18px]">save</span>
                                            Guardar Plantilla
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div className="p-4 px-6 border-t border-slate-100 dark:border-slate-800 flex justify-end bg-slate-50 dark:bg-slate-900/50 shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-2 bg-slate-200 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-white rounded-xl text-xs font-bold transition-all"
                    >
                        Listo
                    </button>
                </div>
            </div>

            {/* Modal de Selección de Unidades Físicas para la Plantilla */}
            {unitSelectorOpen && editingTemplate && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl border border-slate-200 dark:border-slate-800 flex flex-col h-[75vh]">
                        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                            <h3 className="text-base font-bold text-slate-800 dark:text-white">Seleccionar Unidades Físicas para "{editingTemplate.name}"</h3>
                            <button onClick={() => setUnitSelectorOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <div className="flex-1 overflow-auto p-4 space-y-4">
                            {levels.map(level => {
                                const levelUnits = units.filter(u => u.levelId === level.id);
                                const selectedCount = levelUnits.filter(u => editingTemplate.unitIds.includes(u.id)).length;
                                const isAllSelected = selectedCount === levelUnits.length && levelUnits.length > 0;

                                return (
                                    <div key={level.id} className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden bg-white dark:bg-slate-800">
                                        <div className="bg-slate-50 dark:bg-slate-800/50 p-3 flex items-center justify-between border-b border-slate-200 dark:border-slate-700">
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="checkbox"
                                                    className="size-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                                    checked={isAllSelected}
                                                    onChange={(e) => {
                                                        const currentIds = editingTemplate.unitIds || [];
                                                        const levelUnitIds = levelUnits.map(u => u.id);
                                                        const nextIds = e.target.checked
                                                            ? Array.from(new Set([...currentIds, ...levelUnitIds]))
                                                            : currentIds.filter(id => !levelUnitIds.includes(id));
                                                        setEditingTemplate({ ...editingTemplate, unitIds: nextIds });
                                                    }}
                                                />
                                                <span className="font-bold text-slate-800 dark:text-white text-xs">{level.name}</span>
                                            </div>
                                            <span className="text-xs text-slate-500">{selectedCount}/{levelUnits.length} seleccionadas</span>
                                        </div>
                                        <div className="p-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                                            {levelUnits.map(unit => {
                                                const checked = editingTemplate.unitIds.includes(unit.id);
                                                return (
                                                    <label key={unit.id} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors border text-xs ${
                                                        checked 
                                                            ? 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300 font-semibold'
                                                            : 'hover:bg-slate-50 dark:hover:bg-slate-700/50 border-transparent text-slate-700 dark:text-slate-300'
                                                    }`}>
                                                        <input
                                                            type="checkbox"
                                                            className="size-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                                            checked={checked}
                                                            onChange={(e) => {
                                                                const currentIds = editingTemplate.unitIds || [];
                                                                const nextIds = e.target.checked
                                                                    ? [...currentIds, unit.id]
                                                                    : currentIds.filter(id => id !== unit.id);
                                                                setEditingTemplate({ ...editingTemplate, unitIds: nextIds });
                                                            }}
                                                        />
                                                        <span className="truncate">{unit.name}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end bg-slate-50 dark:bg-slate-800/50">
                            <button
                                type="button"
                                onClick={() => setUnitSelectorOpen(false)}
                                className="px-6 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-md transition-colors"
                            >
                                Confirmar Unidades
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
